import React, { useState, Component } from 'react';
import { SearchIcon } from 'lucide-react';
interface FilterPanelProps {
  filters: {
    tier: string;
    category: string;
    region: string;
    status: string;
    searchQuery?: string;
    startDate?: string;
    endDate?: string;
    hazardTypes?: string[];
  };
  onFilterChange: (filters: any) => void;
}
const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFilterChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hazardTypes, setHazardTypes] = useState<string[]>(filters.hazardTypes || ['natural', 'port', 'political', 'infrastructure']);
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({
      [e.target.name]: e.target.value
    });
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onFilterChange({
      searchQuery: value
    });
  };
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === 'startDate') {
      setStartDate(value);
      onFilterChange({
        startDate: value
      });
    } else if (name === 'endDate') {
      setEndDate(value);
      onFilterChange({
        endDate: value
      });
    }
  };
  const handleHazardTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const isChecked = e.target.checked;
    let updatedHazardTypes;
    if (isChecked) {
      updatedHazardTypes = [...hazardTypes, value];
    } else {
      updatedHazardTypes = hazardTypes.filter(type => type !== value);
    }
    setHazardTypes(updatedHazardTypes);
    onFilterChange({
      hazardTypes: updatedHazardTypes
    });
  };
  const handleReset = () => {
    // Reset all filters to default values
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    const allHazardTypes = ['natural', 'port', 'political', 'infrastructure'];
    setHazardTypes(allHazardTypes);
    onFilterChange({
      tier: 'all',
      category: 'all',
      region: 'all',
      status: 'all',
      searchQuery: '',
      startDate: '',
      endDate: '',
      hazardTypes: allHazardTypes
    });
  };
  const handleApply = () => {
    // This is already being handled by the individual onChange handlers
    // But we could add additional validation or processing here if needed
    console.log('Filters applied');
  };
  return <div className="p-4 h-full flex flex-col" data-id="element-70">
      <h2 className="text-xl font-bold text-[#333333] mb-6" data-id="element-71">
        Filter Suppliers
      </h2>
      {/* Search */}
      <div className="relative mb-6" data-id="element-72">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-73">
          <SearchIcon size={18} className="text-gray-400" data-id="element-74" />
        </div>
        <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#005a9e] focus:border-[#005a9e] sm:text-sm" placeholder="Search by name or ID" value={searchQuery} onChange={handleSearchChange} data-id="element-75" />
      </div>
      {/* Tier Filter */}
      <div className="mb-4" data-id="element-76">
        <label htmlFor="tier" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-77">
          Supplier Tier
        </label>
        <select id="tier" name="tier" value={filters.tier} onChange={handleChange} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#005a9e] focus:border-[#005a9e] sm:text-sm" data-id="element-78">
          <option value="all" data-id="element-79">All Tiers</option>
          <option value="1" data-id="element-80">Tier 1</option>
          <option value="2" data-id="element-81">Tier 2</option>
          <option value="3" data-id="element-82">Tier 3</option>
        </select>
      </div>
      {/* Category Filter */}
      <div className="mb-4" data-id="element-83">
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-84">
          Category
        </label>
        <select id="category" name="category" value={filters.category} onChange={handleChange} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#005a9e] focus:border-[#005a9e] sm:text-sm" data-id="element-85">
          <option value="all" data-id="element-86">All Categories</option>
          <option value="Electronics" data-id="element-87">Electronics</option>
          <option value="Raw Materials" data-id="element-88">Raw Materials</option>
          <option value="Components" data-id="element-89">Components</option>
          <option value="Packaging" data-id="element-90">Packaging</option>
        </select>
      </div>
      {/* Region Filter */}
      <div className="mb-4" data-id="element-91">
        <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-92">
          Region
        </label>
        <select id="region" name="region" value={filters.region} onChange={handleChange} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#005a9e] focus:border-[#005a9e] sm:text-sm" data-id="element-93">
          <option value="all" data-id="element-94">All Regions</option>
          <option value="North America" data-id="element-95">North America</option>
          <option value="Europe" data-id="element-96">Europe</option>
          <option value="Asia" data-id="element-97">Asia</option>
          <option value="South America" data-id="element-98">South America</option>
        </select>
      </div>
      {/* Status Filter */}
      <div className="mb-4" data-id="element-99">
        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-100">
          Status
        </label>
        <select id="status" name="status" value={filters.status} onChange={handleChange} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#005a9e] focus:border-[#005a9e] sm:text-sm" data-id="element-101">
          <option value="all" data-id="element-102">All Statuses</option>
          <option value="On Schedule" data-id="element-103">On Schedule</option>
          <option value="Delayed" data-id="element-104">Delayed</option>
          <option value="At Risk" data-id="element-105">At Risk</option>
        </select>
      </div>
      {/* Hazard Types Filter */}
      <div className="mb-4" data-id="element-106">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-107">
          Hazard Types
        </label>
        <div className="space-y-2 bg-white p-3 border border-gray-200 rounded-md" data-id="element-108">
          <div className="flex items-center" data-id="element-109">
            <input id="natural-hazard" name="hazardType" type="checkbox" value="natural" checked={hazardTypes.includes('natural')} onChange={handleHazardTypeChange} className="h-4 w-4 text-[#005a9e] focus:ring-[#005a9e] border-gray-300 rounded" data-id="element-110" />
            <label htmlFor="natural-hazard" className="ml-2 block text-sm text-gray-700" data-id="element-111">
              Natural Hazards
            </label>
          </div>
          <div className="flex items-center" data-id="element-112">
            <input id="port-issue" name="hazardType" type="checkbox" value="port" checked={hazardTypes.includes('port')} onChange={handleHazardTypeChange} className="h-4 w-4 text-[#005a9e] focus:ring-[#005a9e] border-gray-300 rounded" data-id="element-113" />
            <label htmlFor="port-issue" className="ml-2 block text-sm text-gray-700" data-id="element-114">
              Port Issues
            </label>
          </div>
          <div className="flex items-center" data-id="element-115">
            <input id="political-issue" name="hazardType" type="checkbox" value="political" checked={hazardTypes.includes('political')} onChange={handleHazardTypeChange} className="h-4 w-4 text-[#005a9e] focus:ring-[#005a9e] border-gray-300 rounded" data-id="element-116" />
            <label htmlFor="political-issue" className="ml-2 block text-sm text-gray-700" data-id="element-117">
              Political Issues
            </label>
          </div>
          <div className="flex items-center" data-id="element-118">
            <input id="infrastructure-issue" name="hazardType" type="checkbox" value="infrastructure" checked={hazardTypes.includes('infrastructure')} onChange={handleHazardTypeChange} className="h-4 w-4 text-[#005a9e] focus:ring-[#005a9e] border-gray-300 rounded" data-id="element-119" />
            <label htmlFor="infrastructure-issue" className="ml-2 block text-sm text-gray-700" data-id="element-120">
              Infrastructure Issues
            </label>
          </div>
        </div>
      </div>
      {/* Date Range */}
      <div className="mb-4" data-id="element-121">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-122">
          Date Range
        </label>
        <div className="flex space-x-2" data-id="element-123">
          <input type="date" name="startDate" value={startDate} onChange={handleDateChange} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#005a9e] focus:border-[#005a9e] sm:text-sm" data-id="element-124" />
          <input type="date" name="endDate" value={endDate} onChange={handleDateChange} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#005a9e] focus:border-[#005a9e] sm:text-sm" data-id="element-125" />
        </div>
      </div>
      {/* Apply/Reset Buttons */}
      <div className="mt-auto flex space-x-2" data-id="element-126">
        <button type="button" onClick={handleReset} className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#005a9e]" data-id="element-127">
          Reset
        </button>
        <button type="button" onClick={handleApply} className="flex-1 bg-[#005a9e] border border-transparent rounded-md shadow-sm py-2 px-4 text-sm font-medium text-white hover:bg-[#004a8e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#005a9e]" data-id="element-128">
          Apply
        </button>
      </div>
    </div>;
};
export default FilterPanel;